import { createContext, useContext, useState, useEffect } from "react";
import { loadFeatureFlags } from "../utils/featureFlagUtils";

/**
 * FeatureFlagsContext - the resolved feature-flag map for the current session.
 *
 * The provider owns the async load (login short-circuit, fetch + resolve, legacy-safe
 * fallback) via loadFeatureFlags(), and exposes the result to the whole app. Consumers read it
 * with useFeatureFlags(); the routing shell reads the same value to render <Loading/> while it's
 * still null.
 *
 * Value is `null` while loading — every consumer either checks `=== null` (the routing gate) or
 * uses optional chaining (featureFlags?.isX), so null degrades to "not ready / all off" and never
 * throws. Legacy main-client surfaces still receive a `featureFlags` prop from the routing shell.
 */
const FeatureFlagsContext = createContext(null);

export const useFeatureFlags = () => useContext(FeatureFlagsContext);

export const FeatureFlagsProvider = ({ children }) => {
  const [featureFlags, setFeatureFlags] = useState(null); // null = loading

  useEffect(() => {
    let stale = false;
    loadFeatureFlags().then((flags) => {
      if (!stale) setFeatureFlags(flags);
    });
    return () => { stale = true; };
  }, []);

  return (
    <FeatureFlagsContext.Provider value={featureFlags}>{children}</FeatureFlagsContext.Provider>
  );
};
